import { decodeSignallingBundle, encodeSignallingBundle, type SignallingBundle } from "./collaboration-protocol";

export type PeerStatus = "new" | "gathering" | "waiting" | "connecting" | "connected" | "disconnected" | "failed" | "closed";

export interface PeerEvents {
  status(status: PeerStatus): void;
  open(): void;
  message(value: string): void;
  close(): void;
}

export class WebRtcPeer {
  private connection: RTCPeerConnection;
  private channel?: RTCDataChannel;
  private closed = false;

  constructor(private readonly events: PeerEvents, stunServers: string[]) {
    this.connection = new RTCPeerConnection({ iceServers: [{ urls: stunServers }] });
    this.connection.addEventListener("connectionstatechange", () => this.handleConnectionState());
    this.connection.addEventListener("iceconnectionstatechange", () => {
      if (this.connection.iceConnectionState === "failed") this.events.status("failed");
    });
    this.connection.addEventListener("datachannel", (event) => this.attachChannel(event.channel));
  }

  async createOffer(sessionId: string, peerId: string): Promise<string> {
    this.attachChannel(this.connection.createDataChannel("linebyline-collaboration", { ordered: true }));
    this.events.status("gathering");
    await this.connection.setLocalDescription(await this.connection.createOffer());
    await waitForIceGathering(this.connection);
    this.events.status("waiting");
    return encodeSignallingBundle(bundleFrom(this.connection.localDescription, "offer", sessionId, peerId));
  }

  async acceptAnswer(encoded: string, sessionId: string, peerId: string): Promise<void> {
    const bundle = await decodeSignallingBundle(encoded);
    if (bundle.kind !== "answer" || bundle.sessionId !== sessionId || bundle.peerId !== peerId) throw new Error("This answer belongs to a different participant or session.");
    this.events.status("connecting");
    await this.connection.setRemoteDescription(bundle.description);
  }

  async answerOffer(encoded: string): Promise<{ answer: string; sessionId: string; peerId: string }> {
    const offer = await decodeSignallingBundle(encoded);
    if (offer.kind !== "offer") throw new Error("A host offer is required.");
    this.events.status("gathering");
    await this.connection.setRemoteDescription(offer.description);
    await this.connection.setLocalDescription(await this.connection.createAnswer());
    await waitForIceGathering(this.connection);
    this.events.status("waiting");
    const answer = await encodeSignallingBundle(bundleFrom(this.connection.localDescription, "answer", offer.sessionId, offer.peerId));
    return { answer, sessionId: offer.sessionId, peerId: offer.peerId };
  }

  send(value: string): boolean {
    if (this.channel?.readyState !== "open") return false;
    this.channel.send(value);
    return true;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.channel?.close();
    this.connection.close();
    this.events.status("closed");
    this.events.close();
  }

  private attachChannel(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.addEventListener("open", () => { this.events.status("connected"); this.events.open(); });
    channel.addEventListener("message", (event) => { if (typeof event.data === "string") this.events.message(event.data); });
    channel.addEventListener("close", () => { if (!this.closed) { this.events.status("disconnected"); this.events.close(); } });
    channel.addEventListener("error", () => this.events.status("failed"));
  }

  private handleConnectionState(): void {
    const state = this.connection.connectionState;
    if (state === "connected") this.events.status("connected");
    else if (state === "connecting") this.events.status("connecting");
    else if (state === "failed") this.events.status("failed");
    else if (state === "disconnected") this.events.status("disconnected");
    else if (state === "closed") this.events.status("closed");
  }
}

function bundleFrom(description: RTCSessionDescription | null, kind: "offer" | "answer", sessionId: string, peerId: string): SignallingBundle {
  if (!description?.sdp) throw new Error("The browser did not create a complete WebRTC description.");
  return { version: 1, kind, sessionId, peerId, description: { type: kind, sdp: description.sdp } };
}

function waitForIceGathering(connection: RTCPeerConnection, timeoutMs = 12_000): Promise<void> {
  if (connection.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new Error("Timed out while gathering network candidates.")), timeoutMs);
    const changed = () => { if (connection.iceGatheringState === "complete") finish(); };
    const finish = (error?: Error) => {
      clearTimeout(timeout);
      connection.removeEventListener("icegatheringstatechange", changed);
      error ? reject(error) : resolve();
    };
    connection.addEventListener("icegatheringstatechange", changed);
  });
}
